import { conditional } from '@silverhand/essentials';
import classNames from 'classnames';
import { forwardRef, useRef } from 'react';
import type { ForwardedRef, ReactNode, HTMLProps, RefObject } from 'react';

import type { HorizontalAlignment, Position } from '@/types/positioning';

import styles from './index.module.scss';

export type TipBubblePlacement = 'top' | 'right' | 'bottom' | 'left';

type Props = Omit<HTMLProps<HTMLDivElement>, 'children'> & {
  readonly children: ReactNode;           // <- trigger/anchor
  readonly content: ReactNode;            // <- tooltip content (คุณใช้อยู่แล้ว)
  readonly position?: Position;
  readonly anchorRef?: RefObject<Element>; // <- ทำให้ optional
  readonly placement?: TipBubblePlacement;
  readonly horizontalAlignment?: HorizontalAlignment;
  readonly className?: string;
  readonly isSuccessful?: boolean;
};

const supportHorizontalAlignmentPlacements = new Set<TipBubblePlacement>(['top', 'bottom']);

function TipBubble(
  {
    children,
    content,
    position,
    placement = 'bottom',
    horizontalAlignment = 'center',
    className,
    anchorRef,
    isSuccessful,
    ...rest
  }: Props,
  reference: ForwardedRef<HTMLDivElement>
) {
  // fallback ref ถ้า caller ไม่ส่ง anchorRef มา
  const fallbackAnchorRef = useRef<HTMLSpanElement | null>(null);
  const resolvedAnchorRef = (anchorRef ?? fallbackAnchorRef) as RefObject<Element>;

  const anchorRect = resolvedAnchorRef.current?.getBoundingClientRect();

  const arrowPosition = conditional(
    supportHorizontalAlignmentPlacements.has(placement) &&
      position &&
      anchorRect && {
        left: anchorRect.x + anchorRect.width / 2 - Number(position.left),
      }
  );

  return (
    <div className={styles.tipWrapper}>
      {/* ตัว anchor/trigger */}
      <span ref={fallbackAnchorRef} className={styles.tipAnchor}>
        {children}
      </span>

      {/* ตัว bubble */}
      <div
        {...rest}
        ref={reference}
        className={classNames(
          styles.tipBubble,
          styles[placement],
          !position && styles.invisible,
          isSuccessful && styles.successful,
          className
        )}
        style={{ ...position }}
      >
        {content}
        <div className={styles.arrow} style={{ ...arrowPosition }} />
      </div>
    </div>
  );
}

export default forwardRef(TipBubble);